import React, { FC, ReactNode } from "react";
import { Popover, Tooltip } from "antd";
import { EntityNormalization } from "../../../annotator/interfaces/entity";
import useEntityConversions from "../../hooks/useEntityConversions";
import { ConversionInputKind, EntityLink } from "../../interfaces/conversion";
import "./EntityConversionTitle.scss";

type Props = {
  entityName: string;
  fallbackTooltip: string;
  entityTypeId?: string;
  entityNormalizations: Array<EntityNormalization> | null | undefined;
  entityLink?: EntityLink;
  children: ReactNode;
};

// Only the config tag carries an icon; entity tags use colour + label.
const INPUT_ICON: Partial<Record<ConversionInputKind, string>> = {
  config: "bi-gear-fill",
};

// Wraps the entity title text: shows the normalization popover when the entity
// has conversions, otherwise falls back to the plain truncation tooltip.
const EntityConversionTitle: FC<Props> = ({
  entityName,
  fallbackTooltip,
  entityTypeId,
  entityNormalizations,
  entityLink,
  children,
}) => {
  const { entityLabel, header, conversions } = useEntityConversions(
    entityTypeId,
    entityNormalizations,
    entityLink,
  );

  if (conversions.length === 0) {
    return <Tooltip title={fallbackTooltip}>{children}</Tooltip>;
  }

  return (
    <Popover
      trigger="hover"
      mouseEnterDelay={0.5}
      placement="bottom"
      overlayClassName="conversion-popover-overlay"
      content={
        <div className="conversion-popover">
          <div className="conversion-popover__eyebrow">{entityLabel}</div>
          <div className="conversion-popover__name">{entityName}</div>
          <div className="conversion-popover__header">{header}</div>
          <ol className="conversion-popover__steps">
            {conversions.map((conversion, index) => (
              <li className="conversion-popover__step" key={index}>
                <span className="conversion-popover__index">{index + 1}</span>
                <div className="conversion-popover__body">
                  <div className="conversion-popover__title">
                    {conversion.title}
                  </div>
                  <div className="conversion-popover__detail">
                    {conversion.detail}
                  </div>
                  {conversion.inputs.map((input, inputIndex) => (
                    <span
                      className={`conversion-tag conversion-tag--${input.kind}`}
                      key={inputIndex}
                    >
                      {INPUT_ICON[input.kind] && (
                        <span
                          className={`conversion-tag__icon bi ${
                            INPUT_ICON[input.kind]
                          }`}
                        />
                      )}
                      <span className="conversion-tag__label">
                        {input.label}
                      </span>
                      <span
                        className={`conversion-tag__value${
                          input.isEmpty ? " conversion-tag__value--empty" : ""
                        }`}
                      >
                        {input.value}
                      </span>
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
      }
    >
      {children}
    </Popover>
  );
};

export default EntityConversionTitle;
